type MockResponseHandler = (
  request: Request,
  params: Record<string, string>,
  next: () => void
) => Promise<Response | null> | Response | null;

type PathPattern = string | RegExp;
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface RouterConfig {
  path: PathPattern;
  method: HttpMethod;
  handler: MockResponseHandler;
}

// Re-defined here to avoid circular dependency with schema lib
type SchemaMethod = (fn: (url: string, reqInit: RequestInit) => Promise<any>, req: any) => ReturnType<typeof fn>;
type SchemaService = Record<string, SchemaMethod>;

const URL_SAFE_CHAR_PATTERN = '[A-Za-z0-9_.\\-~]';

/**
 * Converts a URL pattern with parameters into a regular expression and extracts parameter names.
 *
 * @param pattern - A URL pattern string containing parameters prefixed with ':' (e.g., '/users/:id/posts/:postId')
 * @returns An object containing:
 *  - regex: A RegExp that matches URLs conforming to the pattern
 *  - paramsList: An array of parameter names extracted from the pattern
 *
 * @example
 * // Converting a URL pattern with multiple parameters
 * convertUrlPatternToRegex('/users/:userId/posts/:postId')
 * // Returns: {
 * //   regex: /^\/users\/([a-zA-Z0-9-_~.]+)\/posts\/([a-zA-Z0-9-_~.]+)$/,
 * //   paramsList: ['userId', 'postId']
 * // }
 *
 * @remarks
 * - The function replaces each parameter in the pattern with a capture group
 * - Parameters must consist of URL-safe characters
 * - The resulting regex is anchored with ^ and $ to ensure full string matches
 */
const convertUrlPatternToRegex = (pattern: string): { regex: RegExp; paramsList: string[] } => {
  const paramMatchRegex = new RegExp(`/:(${URL_SAFE_CHAR_PATTERN}+)`, 'g');
  const paramsList: string[] = [];

  const convertedString = pattern.replace(paramMatchRegex, (_, param) => {
    paramsList.push(param);
    return `/(${URL_SAFE_CHAR_PATTERN}+)`;
  });

  return {
    regex: new RegExp(`^${convertedString}$`),
    paramsList,
  };
};

export class MockAPIRouter {
  private routes: RouterConfig[] = [];

  /**
   * Parse path parameters from a URL pattern
   * @param pattern URL pattern with parameters like '/users/:id'
   * @param url Actual URL path to match against
   * @returns Record of parameter names and values, or null if no match
   */
  private parseParams(pattern: string, url: string): Record<string, string> | null {
    const { paramsList, regex } = convertUrlPatternToRegex(pattern);
    const match = url.match(regex);

    if (!match) return null;

    const params: Record<string, string> = {};
    match.slice(1).forEach((value, index) => {
      params[paramsList[index]] = decodeURIComponent(value);
    });

    return params;
  }

  /**
   * Match a request against all defined routes
   * @param request The Request object to match
   * @returns A response from the matched route handler or null if no match
   */
  async match(request: Request): Promise<Response | null> {
    const url = new URL(request.url);
    const pathname = url.pathname;

    let currentRouteIndex = 0;

    const processNextRoute = async (): Promise<Response | null> => {
      // No more routes to process
      if (currentRouteIndex >= this.routes.length) {
        return null;
      }

      const route = this.routes[currentRouteIndex++];

      // Check if method matches
      if (route.method !== request.method) {
        return processNextRoute();
      }

      // Check if path matches
      let params: Record<string, string> = {};
      let matched = false;

      if (typeof route.path === 'string') {
        // Handle path parameters like '/users/:id'
        if (route.path.includes(':')) {
          const parsedParams = this.parseParams(route.path, pathname);
          if (parsedParams) {
            params = parsedParams;
            matched = true;
          }
        } else {
          matched = pathname === route.path;
        }
      } else if (route.path instanceof RegExp) {
        matched = route.path.test(pathname);
      }

      if (!matched) {
        return processNextRoute();
      }

      // Path matched, call handler with next() function
      let nextCalled = false;
      const next = () => {
        nextCalled = true;
      };

      try {
        const result = await route.handler(request, params, next);

        // If handler returned a response, use it
        if (result) {
          return result;
        }

        // If next() was called or no response was returned, process next route
        if (nextCalled) {
          return processNextRoute();
        }

        // No response and next() not called - return null
        return null;
      } catch (error) {
        console.error('Error in mock route handler:', error);
        return new Response(JSON.stringify({ error: 'Mock handler error' }), {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    };

    return processNextRoute();
  }

  /**
   * Add a route with the specified method
   * @param method HTTP method ('GET', 'POST', etc.)
   * @param path URL path pattern or RegExp
   * @param handler Response handler function
   */
  private addRoute(method: HttpMethod, path: PathPattern, handler: MockResponseHandler) {
    const existingRouteIndex = this.routes.findIndex((route) => {
      return route.method === method && route.path === path;
    });

    if (existingRouteIndex === -1) {
      this.routes.push({ method, path, handler });
    } else {
      this.routes[existingRouteIndex].handler = handler;
    }
  }

  /**
   * Define a Schema Endpoint route
   * @param endpoint Schema service method
   * @param handler Response handler function
   */
  schemaEndpoint(endpoint: SchemaService[keyof SchemaService], handler: MockResponseHandler): this {
    const placeholderRequest = new Proxy(
      {},
      {
        get: (_target, prop) => `:${String(prop)}`,
      }
    );

    endpoint(async (endpointUrl, { method = 'GET' }) => {
      this.addRoute(method as HttpMethod, endpointUrl.split('?')[0], handler);
    }, placeholderRequest);

    return this;
  }

  /**
   * Define a GET route
   * @param path URL path pattern or RegExp
   * @param handler Response handler function
   */
  get(path: PathPattern, handler: MockResponseHandler): this {
    this.addRoute('GET', path, handler);
    return this;
  }

  /**
   * Define a POST route
   * @param path URL path pattern or RegExp
   * @param handler Response handler function
   */
  post(path: PathPattern, handler: MockResponseHandler): this {
    this.addRoute('POST', path, handler);
    return this;
  }

  /**
   * Define a PUT route
   * @param path URL path pattern or RegExp
   * @param handler Response handler function
   */
  put(path: PathPattern, handler: MockResponseHandler): this {
    this.addRoute('PUT', path, handler);
    return this;
  }

  /**
   * Define a DELETE route
   * @param path URL path pattern or RegExp
   * @param handler Response handler function
   */
  delete(path: PathPattern, handler: MockResponseHandler): this {
    this.addRoute('DELETE', path, handler);
    return this;
  }

  /**
   * Helper method that creates JSON responses
   * @param data Response data
   * @param status HTTP status code
   * @returns Response object
   */
  static json(data: any, status = 200): Response {
    return new Response(JSON.stringify(data), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Helper method that creates text responses
   * @param text Response text
   * @param status HTTP status code
   * @returns Response object
   */
  static text(text: string, status = 200): Response {
    return new Response(text, {
      status,
      headers: { 'Content-Type': 'text/plain' },
    });
  }
}
